// src/pages/message-center/StaffCommissionPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Users2, Wallet } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel, StatsCards } from '../../components/common';
import MonthPicker from '../../components/ui/MonthPicker/MonthPicker';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getStaffCommissionReport } from '../../services/api/messageCenterCommission';
import { getUsers } from '../../services/api/users';
import { getShiftTypeIcon } from './utils/shiftTypeIcon';
import styles from './StaffCommissionPage.module.css';

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const StaffCommissionPage = () => {
  const { t, formatCurrency, formatDate } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const canViewAll = hasPermission('view_all_message_center_commission');

  const [rows, setRows] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ month: currentMonth(), userIds: [] });

  useEffect(() => {
    if (canViewAll) {
      getUsers({ status: 'true', page: 1, limit: 200 })
        .then((data) => setBusinessUsers(data.users || []))
        .catch((err) => console.error('Error loading business users:', err));
    }
  }, [canViewAll]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = { month: filters.month };
      if (canViewAll && filters.userIds.length > 0) params.userIds = filters.userIds.join(',');
      const data = await getStaffCommissionReport(params);
      setRows(data.rows || []);
    } catch (err) {
      console.error('Error loading staff commission report:', err);
      showError(err.response?.data?.message || t('messageCenterCommission.failedToLoadStaffReport'));
    } finally {
      setIsLoading(false);
    }
  }, [filters, canViewAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totalCommission = rows.reduce((sum, row) => sum + (Number(row.commissionAmount) || 0), 0);

  const statsCardsData = [
    {
      key: 'totalCommission',
      label: t('messageCenterCommission.totalCommission'),
      value: totalCommission,
      icon: Wallet,
      color: '#7F5CFF',
      format: (value) => formatCurrency(value),
      loading: isLoading,
    },
  ];

  const filterDefinitions = [
    {
      key: 'month',
      label: t('messageCenterCommission.filterByMonth'),
      type: 'custom',
      render: (value, onChange) => <MonthPicker value={value} onChange={onChange} />,
    },
    ...(canViewAll
      ? [
          {
            key: 'userIds',
            label: t('messageCenterCommission.filterByUser'),
            type: 'custom',
            render: (value, onChange) => (
              <MultiSelectFilter
                options={businessUsers}
                selectedValues={value || []}
                onChange={onChange}
                placeholder={t('messageCenterCommission.allUsers')}
                displayKey="name"
                valueKey="id"
                searchKey="name"
              />
            ),
          },
        ]
      : []),
  ];

  const columns = [
    { key: 'shiftDate', header: t('messageCenterShifts.shiftDate'), width: '130px', render: (row) => formatDate(row.shiftDate) },
    {
      key: 'shiftType',
      header: t('messageCenterShifts.shiftType'),
      width: '160px',
      render: (row) => {
        if (!row.shiftTypeName) return <span className={styles.na}>—</span>;
        const ShiftTypeIcon = getShiftTypeIcon(row.shiftTypeName);
        return (
          <div className={styles.shiftTypeCell}>
            <ShiftTypeIcon size={14} />
            <span>{row.shiftTypeName}</span>
          </div>
        );
      },
    },
    ...(canViewAll ? [{ key: 'userName', header: t('messageCenterCommission.staffMember'), width: '220px', render: (row) => row.userName || '—' }] : []),
    { key: 'startTime', header: t('messageCenterShifts.startTime'), width: '110px', render: (row) => row.startTime },
    { key: 'endTime', header: t('messageCenterShifts.endTime'), width: '110px', render: (row) => row.endTime },
    {
      key: 'commissionAmount',
      header: t('messageCenterCommission.commissionAmount'),
      width: '160px',
      render: (row) => (
        <div className={styles.amountCell}>
          {formatCurrency(row.commissionAmount)}
          {row.isEstimate && <span className={styles.estimateBadge}>{t('messageCenterCommission.estimate')}</span>}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.staffCommissionPage}>
      <PageHeader
        title={t('messageCenterCommission.staffPageTitle')}
        description={t('messageCenterCommission.staffPageDescription')}
        icon={Users2}
      />

      <StatsCards stats={statsCardsData} columns={1} minColumnWidth="220px" />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={setFilters}
        onClear={() => setFilters({ month: currentMonth(), userIds: [] })}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
      />

      <DataTable
        data={rows}
        columns={columns}
        loading={isLoading}
        emptyMessage={t('messageCenterCommission.noStaffCommissionRecords')}
        emptyIcon={<Users2 size={64} />}
      />
    </div>
  );
};

export default StaffCommissionPage;
